import React, { useState, useEffect } from 'react';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import TenantDialog from '../components/TenantDialog';
import DeployDialog from '../components/DeployDialog';
import SubscriptionsPage from './SubscriptionsPage';
import { tenantsService } from '../services/api';

export default function DashboardPage({ onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Load environment from localStorage or default to 'test'
  const [environment, setEnvironment] = useState(() => {
    return localStorage.getItem('admin_environment') || 'test';
  });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [section, setSection] = useState(0); // 0 = Tenants, 1 = Subscriptions
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error' | 'info' | 'warning'
  });

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await tenantsService.getAll(environment);
      setTenants(data);
    } catch (err) {
      // Extract the error message from axios response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load tenants';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEnvironmentChange = (event) => {
    const newEnv = event.target.value;
    setEnvironment(newEnv);
    localStorage.setItem('admin_environment', newEnv);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchTenants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment]);

  const handleCreate = () => {
    setSelectedTenant(null);
    setDialogOpen(true);
  };

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setDialogOpen(true);
  };

  const handleDeploy = (tenant) => {
    setSelectedTenant(tenant);
    setDeployDialogOpen(true);
  };

  const handleSave = async (tenantData) => {
    try {
      if (selectedTenant) {
        await tenantsService.update(selectedTenant.id, tenantData, environment);
        showSnackbar(`Tenant "${tenantData.name}" updated successfully`);
      } else {
        await tenantsService.create(tenantData, environment);
        showSnackbar(`Tenant "${tenantData.name}" created successfully`);
      }
      setDialogOpen(false);
      fetchTenants();
    } catch (err) {
      // Extract the error message from axios response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save tenant';
      throw new Error(errorMessage);
    }
  };

  const handleDeploySubmit = async (deployEnvironment) => {
    try {
      await tenantsService.deploy(selectedTenant.tenant, deployEnvironment, environment);
      showSnackbar(`Tenant "${selectedTenant.name}" deployed to ${deployEnvironment.toUpperCase()} successfully`);
      setDeployDialogOpen(false);
      fetchTenants();
    } catch (err) {
      // Extract the error message from axios response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to deploy tenant';
      throw new Error(errorMessage);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Betterfield Admin
          </Typography>

          {/* Environment Selector */}
          <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
            <Select
              value={environment}
              onChange={handleEnvironmentChange}
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'white',
                },
                '.MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            >
              <MenuItem value="test">Test</MenuItem>
              <MenuItem value="qa">QA</MenuItem>
              <MenuItem value="prod">Production</MenuItem>
            </Select>
          </FormControl>

          <IconButton color="inherit" onClick={fetchTenants} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <IconButton color="inherit" onClick={onLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Section Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="xl">
          <Tabs value={section} onChange={(_, v) => setSection(v)}>
            <Tab label="Tenants" />
            <Tab label="Subscriptions" />
          </Tabs>
        </Container>
      </Box>

      {/* Main Content */}
      {section === 0 ? (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Tenants
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              size={isMobile ? 'small' : 'medium'}
            >
              New Tenant
            </Button>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    {!isMobile && <TableCell>Abbreviation</TableCell>}
                    <TableCell>Tenant ID</TableCell>
                    {!isMobile && <TableCell>Last Access</TableCell>}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : 5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No tenants found. Create your first tenant to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tenants.map((tenant) => (
                      <TableRow key={tenant.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {tenant.name}
                          </Typography>
                          {isMobile && tenant.lastAccessAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDistanceToNow(new Date(tenant.lastAccessAt), { addSuffix: true })}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Chip label={tenant.abbreviation} size="small" />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {tenant.tenant}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            {tenant.lastAccessAt ? (
                              <Typography variant="body2" color="text.secondary">
                                {formatDistanceToNow(new Date(tenant.lastAccessAt), { addSuffix: true })}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                Never
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleDeploy(tenant)}
                            color="primary"
                            title="Deploy"
                          >
                            <RocketIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(tenant)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>
      ) : (
        <SubscriptionsPage tenants={tenants} env={environment} />
      )}

      {/* Dialogs */}
      <TenantDialog
        open={dialogOpen}
        tenant={selectedTenant}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />

      <DeployDialog
        open={deployDialogOpen}
        tenant={selectedTenant}
        currentEnvironment={environment}
        onClose={() => setDeployDialogOpen(false)}
        onDeploy={handleDeploySubmit}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
